import { LitElement, html, css } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { ifDefined } from 'lit/directives/if-defined.js';

import { LIT_CSS_VARIABLES } from '@Root/lib/lit-styles.js';
import { SHARED_ELEMENTS, SHARED_CLASSES } from '../../styles';
import { imagebossUrl } from '../../../../helpers/imageboss';
import { withLineBreaks } from '../../../../helpers/text';
import { handleMediaItemClick } from '../../../../helpers/adobe-analytics';

// The Figma "Foundation Card" driver card: image driver + title +
// description, with any block-specific extras (CTAs, text sections) slotted
// in below so their markup and analytics stay owned by the block.
//
// Styling contract:
// - The host is the card box (flex column) — blocks size/place it like any
//   card (grid-column: span N, etc.).
// - Knobs: --driver-card-gap, --driver-card-image-aspect.
// - Instance overrides go through parts: ::part(image), ::part(title),
//   ::part(description).
// - Analytics: clicks route through the shared navigate-or-download handler;
//   `placement` labels NAV events, `download-placement` (defaults to
//   placement) labels download events.
class DriverCard extends LitElement {
  static get properties() {
    return {
      card: { type: Object },
      placement: { type: String },
      downloadPlacement: {
        type: String,
        attribute: 'download-placement',
      },
      imageWidth: { type: Number, attribute: 'image-width' },
      imageHeight: { type: Number, attribute: 'image-height' },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      css`
        :host {
          display: flex;
          flex-direction: column;
          gap: var(
            --driver-card-gap,
            calc(16rem / var(--default-rem-px-count))
          );
          min-width: 0;
        }

        a.image {
          display: block;
        }

        img {
          display: block;
          width: var(--driver-card-image-width, 100%);
          max-width: 100%;
          aspect-ratio: var(--driver-card-image-aspect, auto);
          object-fit: var(--driver-card-image-fit, cover);
        }

        /* Foundation Card titles are H4-scale medium labels. */
        .title {
          font-size: var(--fs-label);
          line-height: var(--lh-label);
          letter-spacing: var(--ls-label);
          font-weight: var(--fw-medium);
        }

        .description {
          font-size: var(--fs-body-small);
          line-height: var(--lh-body-small);
          letter-spacing: var(--ls-body-small);
        }
      `,
    ];
  }

  constructor() {
    super();
    this.card = {};
    this.placement = '';
    this.downloadPlacement = '';
    this.imageWidth = null;
    this.imageHeight = null;
  }

  render() {
    return html`
      ${this.imageHTML} ${this.titleHTML} ${this.descriptionHTML}
      <slot></slot>
    `;
  }

  get imageHTML() {
    const src = this.card.src || this.card.image?.src;
    if (!src) return html``;
    return html`
      <a
        part="image"
        class="image"
        href=${ifDefined(this.imageHref)}
        rel="noopener"
        @click="${event => this.handleImageClick(event)}"
      >
        <img
          src=${imagebossUrl(src, {
            width: this.imageWidth,
            height: this.imageHeight,
          })}
        />
      </a>
    `;
  }

  get titleHTML() {
    if (!this.card.title) return html``;
    return html`
      <h3 part="title" class="title">
        ${unsafeHTML(withLineBreaks(this.card.title))}
      </h3>
    `;
  }

  get descriptionHTML() {
    if (!this.card.description) return html``;
    return html`
      <p part="description" class="description">
        ${unsafeHTML(withLineBreaks(this.card.description))}
      </p>
    `;
  }

  get imageHref() {
    return this.card.url || this.card.full_resolution_src || undefined;
  }

  handleImageClick(event) {
    handleMediaItemClick(this.card, event, {
      navPlacement: this.placement,
      downloadPlacement: this.downloadPlacement || this.placement,
    });
  }
}

customElements.define('driver-card', DriverCard);

export { DriverCard };
